import { FILE_THUMBNAIL_MAX_COUNT, FILE_THUMBNAIL_TTL_MS } from './schema';
import { db } from './index';

/**
 * Returns the decrypted blob for a file thumbnail if it exists and is within TTL (24h).
 * Deletes the entry if it is expired and returns null.
 */
export async function getFileThumbnailBlob(fileId: number): Promise<Blob | null> {
  try {
    const fileThumbnailEntry = await db.fileThumbnails.get(fileId);
    if (!fileThumbnailEntry) return null;

    const now = Date.now();
    if (fileThumbnailEntry.savedAt < now - FILE_THUMBNAIL_TTL_MS) {
      await db.fileThumbnails.delete(fileId);
      return null;
    }

    return fileThumbnailEntry.blob;
  } catch {
    return null;
  }
}

/**
 * Saves a file thumbnail blob and runs eviction: remove entries older than TTL, then if count > MAX_COUNT remove oldest by savedAt.
 */
export async function setFileThumbnailBlob(fileId: number, blob: Blob): Promise<void> {
  try {
    const savedAt = Date.now();
    await db.fileThumbnails.put({ fileId, blob, savedAt });

    // Remove entries older than TTL
    const cutoff = savedAt - FILE_THUMBNAIL_TTL_MS;
    const expired = await db.fileThumbnails.where('savedAt').below(cutoff).toArray();
    if (expired.length > 0) {
      await db.fileThumbnails.bulkDelete(expired.map((e) => e.fileId));
    }

    // If over max count, remove oldest by savedAt
    const fileThumbnailCount = await db.fileThumbnails.count();
    if (fileThumbnailCount > FILE_THUMBNAIL_MAX_COUNT) {
      const toRemove = fileThumbnailCount - FILE_THUMBNAIL_MAX_COUNT;
      const oldest = await db.fileThumbnails.orderBy('savedAt').limit(toRemove).toArray();
      await db.fileThumbnails.bulkDelete(oldest.map((e) => e.fileId));
    }
  } catch {
    // No-op on quota or DB errors; in-memory and API path still work
  }
}

/**
 * Clears all file thumbnail blobs (e.g. on logout).
 */
export async function clearFileThumbnails(): Promise<void> {
  try {
    await db.fileThumbnails.clear();
  } catch {
    // No-op
  }
}
